// src/pages/PricingPlans.jsx - COMPLETELY REDESIGNED
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PricingPlans = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'

  const plans = [
    {
      id: 'basic',
      name: 'BASIC',
      tagline: 'Perfect for getting started',
      monthlyPrice: 3000,
      yearlyPrice: 31000,
      icon: 'bx-rocket',
      features: [
        'AI SEARCH',
        'CHAT BOT - Ask Unlimited Questions',
        'Chat History',
        'Generate a Draft',
        'Search Suggestions (TOPIC)',
        'Chat box in judgment page',
        'Advance Search (All Parameters)',
        'Translation in Multiple Languages',
        'Basic Searches like Citation Search'
      ],
      database: [
        'Supreme Court & SINGLE High Court',
        'Privy Council Judgments',
        'Historical Courts',
        'Federal Courts',
        'Tribunals & Commissions',
        'Central & State Laws',
        'Latest News & Law Points',
        'Latest Amendments',
        'Articles & Reports'
      ],
      popular: false,
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)'
    },
    {
      id: 'premium',
      name: 'PREMIUM',
      tagline: 'Most Popular Choice',
      monthlyPrice: 5000,
      yearlyPrice: 51000,
      icon: 'bx-crown',
      features: [
        'All Features in Basic',
        'VIRTUAL CHAT ASSISTANT',
        'Create Own Digests',
        'Criminal Module (Acquittal & Conviction)',
        'Priority Support',
        'Advanced Analytics',
        'Custom Filters',
        'Export Options'
      ],
      database: [
        'All High Courts',
        'All Tribunals',
        'Historical & Federal Courts',
        'Complete Central & State Laws',
        'Legal Drafts Hub',
        'Prosecution Favour Page',
        'Original Journals True Prints',
        'Enhanced Search Capabilities'
      ],
      popular: true,
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #c41849 100%)'
    },
    {
      id: 'pro',
      name: 'PRO',
      tagline: 'For professionals who need everything',
      monthlyPrice: 7000,
      yearlyPrice: 71000,
      icon: 'bx-trophy',
      features: [
        'All Features in Premium',
        'International Case-Laws',
        'International Treaties',
        'Policy Documents',
        'Foreign Legislations',
        'Student Text Books',
        'E-Law Books & Commentaries',
        'E-Bare Acts',
        'Dedicated Account Manager'
      ],
      database: [
        'Complete Indian Database',
        'International Case Laws',
        'Foreign Legislations',
        'Policy Documents',
        'E-Books & Commentaries',
        'Student Resources',
        'Unlimited Downloads',
        'API Access'
      ],
      popular: false,
      color: '#c41849',
      gradient: 'linear-gradient(135deg, #c41849 0%, #F06292 100%)'
    }
  ];

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    navigate('/signup', { state: { selectedPlan: planId } });
  };

  const getPrice = (plan) => {
    const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <>
      <Header />
      <div className="modern-pricing-page">
        {/* Hero Section */}
        <section className="pricing-hero">
          <div className="hero-decoration decoration-1"></div>
          <div className="hero-decoration decoration-2"></div>
          <div className="hero-decoration decoration-3"></div>
          
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge">
                <i className="bx bx-purchase-tag"></i>
                <span>Pricing Plans</span>
              </div>
              
              <h1 className="hero-title">
                Choose Your <span className="gradient-text">Perfect Plan</span>
              </h1>
              
              <p className="hero-description">
                Unlock the power of AI-driven legal research with plans designed for every legal professional
              </p>

              {/* Billing Toggle */}
              <div className="billing-toggle">
                <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                <button 
                  className="toggle-switch"
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                >
                  <div className={`toggle-slider ${billingCycle === 'yearly' ? 'yearly' : 'monthly'}`}></div>
                </button>
                <span className={billingCycle === 'yearly' ? 'active' : ''}>
                  Yearly
                  <span className="save-badge">Save 15%</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pricing-cards-section">
          <div className="container">
            <div className="pricing-cards-grid">
              {plans.map((plan, index) => (
                <div 
                  key={plan.id} 
                  className={`pricing-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {plan.popular && (
                    <div className="popular-badge">
                      <i className="bx bx-star"></i>
                      <span>Most Popular</span>
                    </div>
                  )}

                  <div className="card-header">
                    <div className="plan-icon" style={{ background: plan.gradient }}>
                      <i className={`bx ${plan.icon}`}></i>
                    </div>
                    
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-tagline">{plan.tagline}</p>
                  </div>

                  <div className="card-pricing">
                    <div className="price-container">
                      <span className="currency">₹</span>
                      <span className="price">{billingCycle === 'monthly' ? plan.monthlyPrice.toLocaleString('en-IN') : plan.yearlyPrice.toLocaleString('en-IN')}</span>
                      <span className="period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <p className="billing-note">Billed annually</p>
                    )}
                  </div>

                  <button 
                    className="select-plan-btn"
                    style={{ background: plan.gradient }}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {selectedPlan === plan.id ? (
                      <>
                        <i className="bx bx-check-circle"></i>
                        <span>Selected Plan</span>
                      </>
                    ) : (
                      <>
                        <span>Get Started</span>
                        <i className="bx bx-right-arrow-alt"></i>
                      </>
                    )}
                  </button>

                  <div className="card-features">
                    <div className="features-section">
                      <h4 className="features-title">
                        <i className="bx bx-check-shield"></i>
                        Key Features
                      </h4>
                      <ul className="features-list">
                        {plan.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx}>
                            <i className="bx bx-check"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 6 && (
                          <li className="more-features">
                            <i className="bx bx-plus"></i>
                            <span>{plan.features.length - 6} more features</span>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="features-section">
                      <h4 className="features-title">
                        <i className="bx bx-data"></i>
                        Database Access
                      </h4>
                      <ul className="features-list">
                        {plan.database.slice(0, 4).map((db, idx) => (
                          <li key={idx}>
                            <i className="bx bx-check"></i>
                            <span>{db}</span>
                          </li>
                        ))}
                        {plan.database.length > 4 && (
                          <li className="more-features">
                            <i className="bx bx-plus"></i>
                            <span>{plan.database.length - 4} more databases</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        {/* <section className="comparison-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Detailed Feature Comparison
              </h2>
              <p className="section-description">
                Compare all features across plans to find your perfect match
              </p>
            </div>

            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th className="feature-col">Features</th>
                    <th className="plan-col">BASIC</th>
                    <th className="plan-col popular">PREMIUM</th>
                    <th className="plan-col">PRO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="feature-name">AI Search</td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Chat Bot</td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Virtual Chat Assistant</td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Criminal Module</td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">International Case Laws</td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">E-Books & Commentaries</td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                  <tr>
                    <td className="feature-name">High Courts Coverage</td>
                    <td>Single</td>
                    <td>All Courts</td>
                    <td>All Courts</td>
                  </tr>
                  <tr>
                    <td className="feature-name">Priority Support</td>
                    <td><i className="bx bx-x cross-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                    <td><i className="bx bx-check check-icon"></i></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section> */}

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Frequently Asked Questions
              </h2>
              <p className="section-description">
                Everything you need to know about our pricing plans
              </p>
            </div>

            <div className="faq-grid">
              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-help-circle"></i>
                </div>
                <h3 className="faq-question">Can I upgrade or downgrade my plan?</h3>
                <p className="faq-answer">
                  Yes! You can upgrade or downgrade at any time. Changes take effect immediately, 
                  and we'll prorate the difference in your next billing cycle.
                </p>
              </div>

              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-time-five"></i>
                </div>
                <h3 className="faq-question">Is there a free trial?</h3>
                <p className="faq-answer">
                  Yes! We offer a 7-day free trial for all plans. No credit card required to start. 
                  Cancel anytime during the trial period.
                </p>
              </div>

              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-refresh"></i>
                </div>
                <h3 className="faq-question">What's your refund policy?</h3>
                <p className="faq-answer">
                  We offer a 30-day money-back guarantee. If you're not satisfied with our service, 
                  contact us within 30 days for a full refund.
                </p>
              </div>

              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-credit-card"></i>
                </div>
                <h3 className="faq-question">What payment methods do you accept?</h3>
                <p className="faq-answer">
                  We accept all major credit cards, debit cards, UPI, net banking, and digital wallets. 
                  All payments are processed securely.
                </p>
              </div>

              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-shield-alt"></i>
                </div>
                <h3 className="faq-question">Is my data secure?</h3>
                <p className="faq-answer">
                  Absolutely! We use bank-level encryption and follow industry best practices to 
                  keep your data safe and secure.
                </p>
              </div>

              <div className="faq-item">
                <div className="faq-icon">
                  <i className="bx bx-support"></i>
                </div>
                <h3 className="faq-question">Do you offer support?</h3>
                <p className="faq-answer">
                  Yes! All plans include email support. Premium and Pro plans get priority support 
                  with faster response times.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-card">
              <div className="cta-content">
                <h2 className="cta-title">Need a Custom Solution?</h2>
                <p className="cta-description">
                  Looking for enterprise features, custom integrations, or special pricing? 
                  Our team is here to create the perfect solution for your organization.
                </p>
                <div className="cta-buttons">
                  <button className="cta-btn primary">
                    <i className="bx bx-phone"></i>
                    <span>Contact Sales</span>
                  </button>
                  <button className="cta-btn secondary">
                    <i className="bx bx-calendar"></i>
                    <span>Schedule Demo</span>
                  </button>
                </div>
              </div>
              <div className="cta-illustration">
                <i className="bx bx-support"></i>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />

      <style>{`
       /* ============================================
     FIX TOP WHITE SPACE - ADD THIS FIRST
  ============================================ */
  body {
    margin: 0 !important;
    padding: 0 !important;
  }

  .modern-pricing-page {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
        /* ============================================
           MODERN PRICING PAGE
        ============================================ */
        .modern-pricing-page {
          background: #ffffff;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .pricing-hero {
          position: relative;
          padding: 120px 0 80px;
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 50%, #c41849 100%);
          overflow: hidden;
        }

        .hero-decoration {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .decoration-1 {
          width: 500px;
          height: 500px;
          top: -150px;
          right: -100px;
          animation: float 25s ease-in-out infinite;
        }

        .decoration-2 {
          width: 350px;
          height: 350px;
          bottom: -100px;
          left: -80px;
          animation: float 20s ease-in-out infinite reverse;
        }

        .decoration-3 {
          width: 250px;
          height: 250px;
          top: 40%;
          left: 15%;
          animation: float 30s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 40px) scale(0.95);
          }
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 50px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-badge i {
          font-size: 18px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          color: white;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .gradient-text {
          background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        /* Billing Toggle */
        .billing-toggle {
          display: inline-flex;
          align-items: center;
          gap: 16px;
          padding: 8px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border-radius: 50px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .billing-toggle span {
          font-size: 16px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.7);
          transition: all 0.3s ease;
          padding: 0 8px;
        }

        .billing-toggle span.active {
          color: white;
        }

        .toggle-switch {
          position: relative;
          width: 60px;
          height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .toggle-slider {
          position: absolute;
          top: 4px;
          width: 24px;
          height: 24px;
          background: white;
          border-radius: 50%;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .toggle-slider.monthly {
          left: 4px;
        }

        .toggle-slider.yearly {
          left: 32px;
        }

        .save-badge {
          display: inline-block;
          margin-left: 8px;
          padding: 4px 10px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
        }

        /* ============================================
           PRICING CARDS
        ============================================ */
        .pricing-cards-section {
          padding: 80px 0;
          margin-top: -60px;
          position: relative;
          z-index: 3;
        }

        .pricing-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 32px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .pricing-card {
          background: white;
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.4s ease;
          position: relative;
          animation: slideUp 0.6s ease both;
          border: 2px solid transparent;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pricing-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .pricing-card.popular {
          border-color: #E91E63;
          box-shadow: 0 8px 30px rgba(245, 166, 35, 0.25);
        }

        .pricing-card.selected {
          border-color: #10b981;
          box-shadow: 0 8px 30px rgba(16, 185, 129, 0.25);
        }

        .popular-badge {
          position: absolute;
          top: -12px;
          right: 32px;
          padding: 8px 20px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 50px;
          color: white;
          font-size: 13px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 4px 12px rgba(245, 166, 35, 0.4);
        }

        .popular-badge i {
          font-size: 16px;
        }

        .card-header {
          text-align: center;
          margin-bottom: 28px;
        }

        .plan-icon {
          width: 72px;
          height: 72px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .plan-icon i {
          font-size: 36px;
          color: white;
        }

        .plan-name {
          font-size: 28px;
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .plan-tagline {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .card-pricing {
          text-align: center;
          margin-bottom: 28px;
          padding-bottom: 28px;
          border-bottom: 2px solid #f0f0f0;
        }

        .price-container {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;
          margin-bottom: 8px;
        }

        .currency {
          font-size: 24px;
          font-weight: 700;
          color: #666;
        }

        .price {
          font-size: 48px;
          font-weight: 800;
          color: #1a1a1a;
          line-height: 1;
        }

        .period {
          font-size: 16px;
          color: #999;
          font-weight: 500;
        }

        .billing-note {
          font-size: 13px;
          color: #999;
          margin: 0;
        }

        .select-plan-btn {
          width: 100%;
          padding: 16px;
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 28px;
          transition: all 0.3s ease;
        }

        .select-plan-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        .select-plan-btn i {
          font-size: 20px;
        }

        .card-features {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .features-section {
          background: #f8f8f8;
          border-radius: 12px;
          padding: 20px;
        }

        .features-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          font-weight: 700;
          color: #333;
          margin-bottom: 16px;
        }

        .features-title i {
          font-size: 20px;
          color: #E91E63;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .features-list li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 14px;
          color: #555;
          line-height: 1.5;
        }

        .features-list li i {
          font-size: 18px;
          color: #10b981;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .features-list li.more-features {
          color: #E91E63;
          font-weight: 600;
        }

        .features-list li.more-features i {
          color: #E91E63;
        }

        /* ============================================
           COMPARISON TABLE
        ============================================ */
        .comparison-section {
          padding: 80px 0;
          background: #f8f8f8;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 16px;
        }

        .section-description {
          font-size: 1.125rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .comparison-table-wrapper {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          overflow-x: auto;
        }

        .comparison-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .comparison-table thead th {
          padding: 20px;
          font-weight: 700;
          font-size: 16px;
          text-align: center;
          border-bottom: 2px solid #f0f0f0;
        }

        .comparison-table thead th.feature-col {
          text-align: left;
          color: #333;
        }

        .comparison-table thead th.plan-col {
          color: #666;
        }

        .comparison-table thead th.popular {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.1));
          border-radius: 12px 12px 0 0;
          color: #E91E63;
        }

        .comparison-table tbody tr {
          transition: background 0.3s ease;
        }

        .comparison-table tbody tr:hover {
          background: #fafafa;
        }

        .comparison-table tbody td {
          padding: 18px 20px;
          border-bottom: 1px solid #f0f0f0;
          text-align: center;
        }

        .comparison-table tbody td.feature-name {
          text-align: left;
          font-weight: 500;
          color: #333;
        }

        .check-icon {
          font-size: 24px;
          color: #10b981;
        }

        .cross-icon {
          font-size: 24px;
          color: #ef4444;
        }

        /* ============================================
           FAQ SECTION
        ============================================ */
        .faq-section {
          padding: 80px 0;
          background: white;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 28px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .faq-item {
          background: #f8f8f8;
          border-radius: 16px;
          padding: 28px;
          transition: all 0.3s ease;
        }

        .faq-item:hover {
          background: white;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-4px);
        }

        .faq-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .faq-icon i {
          font-size: 24px;
          color: white;
        }

        .faq-question {
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 12px;
        }

        .faq-answer {
          font-size: 14px;
          color: #666;
          line-height: 1.7;
          margin: 0;
        }

        /* ============================================
           CTA SECTION
        ============================================ */
        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, #E91E63 0%, #c41849 100%);
        }

        .cta-card {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 24px;
          padding: 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 40px;
        }

        .cta-content {
          flex: 1;
        }

        .cta-title {
          font-size: clamp(2rem, 4vw, 2.5rem);
          font-weight: 800;
          color: white;
          margin-bottom: 16px;
        }

        .cta-description {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .cta-btn.primary {
          background: white;
          color: #E91E63;
        }

        .cta-btn.primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(255, 255, 255, 0.3);
        }

        .cta-btn.secondary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .cta-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-3px);
        }

        .cta-btn i {
          font-size: 20px;
        }

        .cta-illustration {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
          }
        }

        .cta-illustration i {
          font-size: 80px;
          color: white;
        }

        /* ============================================
           RESPONSIVE
        ============================================ */
        @media (max-width: 1024px) {
          .pricing-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          }

          .cta-card {
            flex-direction: column;
            text-align: center;
          }

          .cta-illustration {
            width: 140px;
            height: 140px;
          }

          .cta-illustration i {
            font-size: 60px;
          }
        }

        @media (max-width: 768px) {
          .pricing-hero {
            padding: 80px 0 60px;
          }

          .pricing-cards-section {
            padding: 60px 0;
          }

          .pricing-cards-grid {
            grid-template-columns: 1fr;
          }

          .comparison-section,
          .faq-section,
          .cta-section {
            padding: 60px 0;
          }

          .faq-grid {
            grid-template-columns: 1fr;
          }

          .cta-card {
            padding: 40px 24px;
          }

          .cta-buttons {
            flex-direction: column;
            width: 100%;
          }

          .cta-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
};

export default PricingPlans;